import React, { useEffect } from 'react';

// FAQ Schema generator for SEO
export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Review/Testimonial Schema generator
export const generateReviewSchema = (testimonials, productName = "GetCare 360") => {
  if (!testimonials || testimonials.length === 0) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": productName,
    "description": "AI-powered NDIS provider management software",
    "brand": {
      "@type": "Brand",
      "name": "GetCare 360"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": testimonials.length.toString(),
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": testimonials.slice(0, 5).map(t => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": t.name
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": t.rating || "5",
        "bestRating": "5",
        "worstRating": "1"
      },
      "reviewBody": t.text || t.content
    }))
  };
};

// Article/Blog Schema generator
export const generateArticleSchema = (article) => {
  if (!article) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt || article.description,
    "image": article.image || "https://getcare360.com.au/og-image.jpg",
    "author": {
      "@type": "Organization",
      "name": "GetCare 360"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GetCare 360",
      "logo": {
        "@type": "ImageObject",
        "url": "https://getcare360.com.au/logo.png"
      }
    },
    "datePublished": article.publishedDate || article.created_at,
    "dateModified": article.modifiedDate || article.updated_at || article.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://getcare360.com.au/blog/${article.slug}`
    }
  };
};

// Service Schema generator
export const generateServiceSchema = (service) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.name,
    "provider": {
      "@type": "Organization",
      "name": "GetCare 360"
    },
    "description": service.description,
    "areaServed": {
      "@type": "Country",
      "name": "Australia"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "NDIS Software Solutions",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": service.name
          }
        }
      ]
    }
  };
};

// How-To Schema (for guides/tutorials)
export const generateHowToSchema = (howTo) => {
  if (!howTo || !howTo.steps) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": howTo.title,
    "description": howTo.description,
    "totalTime": howTo.totalTime || "PT30M",
    "step": howTo.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description,
      "url": step.url
    }))
  };
};

// Video Schema (for demo videos)
export const generateVideoSchema = (video) => {
  if (!video) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": video.title,
    "description": video.description,
    "thumbnailUrl": video.thumbnail,
    "uploadDate": video.uploadDate,
    "duration": video.duration,
    "contentUrl": video.url,
    "embedUrl": video.embedUrl
  };
};

// Pricing/Offer Schema
export const generatePricingSchema = (packages) => {
  if (!packages || packages.length === 0) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "GetCare 360 NDIS Software",
    "description": "Custom-built NDIS provider management platform",
    "brand": {
      "@type": "Brand",
      "name": "GetCare 360"
    },
    "offers": packages.map(pkg => ({
      "@type": "Offer",
      "name": pkg.name,
      "price": pkg.price?.toString().replace(/[^0-9]/g, '') || "0",
      "priceCurrency": "AUD",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "description": pkg.description
    }))
  };
};

// Component to inject schema via useEffect for React 19 compatibility
// react-helmet has issues with script children in React 19
const SchemaMarkup = ({ schema }) => {
  useEffect(() => {
    if (!schema) return;
    
    // Create a unique identifier based on schema type
    const schemaType = schema['@type'] || 'Unknown';
    const schemaId = `schema-markup-${schemaType}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Remove any existing schema with the same type
    const existingScripts = document.querySelectorAll(`script[data-schema-type="${schemaType}"]`);
    existingScripts.forEach(script => script.remove());
    
    // Inject the schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema-markup', schemaId);
    script.setAttribute('data-schema-type', schemaType);
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    
    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.querySelector(`script[data-schema-markup="${schemaId}"]`);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [schema]);
  
  return null;
};

export default SchemaMarkup;
